/**
 * MfaBackupEntry — the ONE backup-code field.
 *
 * Public since LIFE-SHORTLISTED-001 D12a; the twin of `<MfaCodeInput>` for a backup code, and built
 * the same way: it composes page-shell's `<FormField>`, follows the one submit-path rule
 * (`hooks/useCodeEntrySubmit.js`), links to the host's alert through `error` + `errorId`, and takes
 * focus on mount only when asked.
 *
 * ## What a backup code is
 *
 * Eight upper-case hex characters (`@puffin-core/verification` `generateBackupCodes`), and the
 * server upper-cases what it is sent before comparing. The field keeps hex only and shows it
 * upper-case, so a character no code can hold is never sent to spend an attempt, and what the person
 * sees matches what they saved. (It took any letter or digit until D12a.)
 *
 * ## It never submits by itself
 *
 * A backup code is spent once, so the person confirms: there is no `autoSubmit` here. Enter submits
 * a complete code.
 *
 * @module @puffin-core/mfa-ui/MfaBackupEntry
 */

import { useCallback, useId, useRef } from 'react';
import PropTypes from 'prop-types';
// The NARROW subpath, never the page-shell barrel (see BackupCodesCard.jsx).
import { FormField } from '@puffin-core/page-shell/form-field';
import { useCodeEntryFocus } from './hooks/useCodeEntryFocus.js';
import { useCodeEntrySubmit } from './hooks/useCodeEntrySubmit.js';

/** Characters in a backup code. */
const CODE_LENGTH = 8;

/**
 * @param {Object} props
 * @param {string} props.code - the current backup code
 * @param {(code: string) => void} props.onChange - receives upper-case hex only, eight at most
 * @param {React.ReactNode} props.label - the visible label; the input's accessible name
 * @param {() => void} [props.onSubmit] - the submit path OUTSIDE a form; never passed inside one
 * @param {string} [props.id] - the input's id; unique per instance when omitted
 * @param {boolean} [props.focusOnMount=false] - take focus when the field mounts
 * @param {boolean} [props.isLoading=false] - an attempt is in flight: Enter submits nothing
 * @param {boolean} [props.disabled] - disables the input; defaults to `isLoading`
 * @param {string|Error|null} [props.error=null] - marks the input invalid; its text is the host's
 * @param {string} [props.errorId] - id of the host element that shows the refusal; read only while
 *   `error` is set
 * @param {string} [props.placeholder]
 * @param {string} [props.data-testid='mfa-backup-input']
 * @returns {React.JSX.Element}
 */
export function MfaBackupEntry({
  code,
  onChange,
  label,
  onSubmit,
  id,
  focusOnMount = false,
  isLoading = false,
  disabled,
  error = null,
  errorId,
  placeholder,
  'data-testid': testId = 'mfa-backup-input',
}) {
  const inputRef = useRef(null);
  const busy = isLoading || Boolean(disabled);

  const handleChange = useCallback(
    (event) =>
      onChange(
        event.target.value
          .replace(/[^0-9a-f]/gi, '')
          .toUpperCase()
          .slice(0, CODE_LENGTH),
      ),
    [onChange],
  );

  // `useId` reads as a custom hook to `web/component-state-order`, so it follows the callback.
  const generatedId = useId();
  useCodeEntryFocus({ inputRef, code, focusOnMount });

  const handleKeyDown = useCodeEntrySubmit({
    inputRef,
    component: 'MfaBackupEntry',
    complete: code.length === CODE_LENGTH && !busy,
    onSubmit,
  });

  return (
    <FormField id={id ?? generatedId} label={label}>
      {(control) => (
        <input
          ref={inputRef}
          id={control.id}
          data-testid={testId}
          className="form-input mfa-code-input"
          type="text"
          maxLength={CODE_LENGTH}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder={placeholder}
          value={code}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled ?? isLoading}
          aria-invalid={Boolean(error)}
          aria-describedby={error && errorId ? errorId : undefined}
        />
      )}
    </FormField>
  );
}

MfaBackupEntry.propTypes = {
  code: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
  onSubmit: PropTypes.func,
  id: PropTypes.string,
  focusOnMount: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
  errorId: PropTypes.string,
  placeholder: PropTypes.string,
  'data-testid': PropTypes.string,
};
